/**
 * Value Creation Component
 * 
 * Displays high-level value creation metrics across three dimensions:
 * - Stakeholder Value (ratio + entropy score)
 * - Business Value (ratio + entropy score)
 * - Customer Value (ratio + entropy score)
 * 
 * Entropy Score: Measures coordination loss (1=best, 10=worst)
 * 
 * Used at the Business (company) level Control Bridge.
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md for terminology definitions
 */

import React from 'react';
import { Tooltip } from 'antd';
import {
  TeamOutlined,
  DollarOutlined,
  UserOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { ValueMetricDisplay, getEntropyInfo, ValueType } from '../../Shared/ValueMetricDisplay';
import styles from './ValueCreationComponent.module.css';

interface ValueMetric {
  type: 'stakeholder' | 'business' | 'customer';
  label: string;
  icon: React.ReactNode;
  color: string;
  ratio: number;
  /** Entropy score (1-10, lower=better). Measures coordination loss. */
  entropyScore: number;
  trend?: 'up' | 'down' | 'stable';
}

type ValueTypeKey = 'stakeholder' | 'business' | 'customer';

interface ValueCreationComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Whether to show compact view */
  compact?: boolean;
  /** Currently selected value type (for detail expansion) */
  selectedValueType?: ValueTypeKey | null;
  /** Callback when a value type is clicked */
  onValueTypeSelect?: (type: ValueTypeKey | null) => void;
}

// Convert backend assessment_rank (higher=better) to entropy score (lower=better)
const toEntropyScore = (assessmentRank: number): number => 11 - assessmentRank;

const getValueMetrics = (data: Record<string, any>): ValueMetric[] => {
  return [
    {
      type: 'stakeholder',
      label: 'Stakeholder Value',
      icon: <TeamOutlined />,
      color: '#8b5cf6', // Purple
      ratio: data.stakeholder_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.stakeholder_assessment_rank ?? 5),
      trend: data.stakeholder_trend as 'up' | 'down' | 'stable' | undefined
    },
    {
      type: 'business',
      label: 'Business Value',
      icon: <DollarOutlined />,
      color: '#10b981', // Green
      ratio: data.business_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.business_assessment_rank ?? 5),
      trend: data.business_trend as 'up' | 'down' | 'stable' | undefined
    },
    {
      type: 'customer',
      label: 'Customer Value',
      icon: <UserOutlined />,
      color: '#3b82f6', // Blue
      ratio: data.customer_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.customer_assessment_rank ?? 5),
      trend: data.customer_trend as 'up' | 'down' | 'stable' | undefined
    }
  ];
};

const TrendIcon: React.FC<{ trend?: 'up' | 'down' | 'stable' }> = ({ trend }) => {
  switch (trend) {
    case 'up':
      return <RiseOutlined style={{ color: '#10b981' }} />;
    case 'down':
      return <FallOutlined style={{ color: '#ef4444' }} />;
    default:
      return <MinusOutlined style={{ color: '#6b7280' }} />;
  }
};

const ValueMetricCard: React.FC<{ 
  metric: ValueMetric; 
  compact?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}> = ({ 
  metric, 
  compact = false,
  isSelected = false,
  onClick
}) => {
  const entropyInfo = getEntropyInfo(metric.entropyScore);
  const isClickable = !!onClick;
  
  return (
    <div 
      className={`${styles.metricCard} ${compact ? styles.compact : ''} ${isSelected ? styles.selected : ''} ${isClickable ? styles.clickable : ''}`}
      style={{ '--metric-color': metric.color } as React.CSSProperties}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
    >
      <div className={styles.metricHeader}>
        <div className={styles.metricIcon} style={{ backgroundColor: metric.color }}>
          {metric.icon}
        </div>
        <div className={styles.metricTitle}>
          <span className={styles.metricLabel}>{metric.label}</span>
          {metric.trend && (
            <Tooltip title={`Trend: ${metric.trend}`}>
              <span className={styles.trendIcon}>
                <TrendIcon trend={metric.trend} />
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <div className={styles.metricBody}>
        <ValueMetricDisplay
          valueRatio={metric.ratio}
          entropyScore={metric.entropyScore}
          valueType={metric.type as ValueType}
          variant={compact ? 'compact' : 'standard'}
          showLabels={!compact}
        />
      </div>
      
      {isClickable && (
        <div className={styles.expandIndicator}>
          {isSelected ? 'Click to collapse' : 'Click for details'}
        </div>
      )}
    </div>
  );
};

export const ValueCreationComponent: React.FC<ValueCreationComponentProps> = ({
  compact = false,
  selectedValueType,
  onValueTypeSelect,
  ...props
}) => {
  const handleCardClick = (type: ValueTypeKey) => {
    if (!onValueTypeSelect) return;
    
    // Toggle selection: if already selected, deselect; otherwise select
    if (selectedValueType === type) {
      onValueTypeSelect(null);
    } else {
      onValueTypeSelect(type);
    }
  };

  const renderContent = (data: ComponentData, loading: boolean) => {
    const metrics = getValueMetrics(data.data);
    
    // Find worst performing metric (highest entropy = most coordination loss)
    const worstPerformer = metrics.reduce((worst, m) => m.entropyScore > worst.entropyScore ? m : worst);
    
    // Check if balanced (all within 2 entropy points of each other)
    const scores = metrics.map(m => m.entropyScore);
    const isBalanced = Math.max(...scores) - Math.min(...scores) <= 2;
    
    return (
      <div className={`${styles.valueCreationComponent} ${compact ? styles.compact : ''}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>Value Creation</h3>
          <span className={styles.artifactName}>{data.artifact_name}</span>
        </div>

        <div className={styles.metricsGrid}>
          {metrics.map((metric) => (
            <ValueMetricCard 
              key={metric.type} 
              metric={metric} 
              compact={compact}
              isSelected={selectedValueType === metric.type}
              onClick={onValueTypeSelect ? () => handleCardClick(metric.type) : undefined}
            />
          ))}
        </div>

        {/* Summary Section */}
        {!compact && (
          <div className={styles.summary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Overall Balance</span>
              <span className={styles.summaryValue}>
                {isBalanced ? 'Balanced' : 'Needs Attention'}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Highest Entropy</span>
              <span className={styles.summaryValue}>
                {worstPerformer.label}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <BaseReportingComponent
      {...props}
      componentType="value_creation"
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ValueCreationComponent;
